import type { Equipment, EquipmentStatus } from '../api/types'
import { useAuthedBlob } from '../lib/photos'

/**
 * De smådele, værktøjslisten og værktøjets egen side deler (beslutning 096).
 *
 * De står her frem for i hver sin fil, fordi de to sider skal se ens ud: en
 * defekt boremaskine skal have samme røde mærkat begge steder, ellers tror man,
 * det betyder to forskellige ting.
 */

const STATUS_COLORS: Record<EquipmentStatus, { background: string; color: string }> = {
  ok: { background: '#DCFCE7', color: '#166534' },
  service: { background: '#FEF3C7', color: '#92400E' },
  defect: { background: '#FEE2E2', color: '#991B1B' }
}

/** Tilstanden som et farvet mærkat. "I orden" vises kun, når man beder om det. */
export function EquipmentStatusBadge({
  status,
  label,
  showOk = false
}: {
  status: EquipmentStatus
  label: string
  /** Vis også det grønne "I orden" - på værktøjets egen side, hvor man spørger. */
  showOk?: boolean
}) {
  if (status === 'ok' && !showOk) return null

  const colors = STATUS_COLORS[status] ?? STATUS_COLORS.ok

  return (
    <span
      className="tiny"
      style={{
        padding: '2px 8px',
        borderRadius: 999,
        fontWeight: 600,
        whiteSpace: 'nowrap',
        ...colors
      }}
    >
      {label}
    </span>
  )
}

/**
 * Fotoet af grejet. Billedet ligger bag login og kan derfor ikke sættes direkte
 * i <img src> - det hentes som blob, præcis som profilbilleder.
 *
 * Uden foto vises et værktøjsikon i samme størrelse, så listen ikke hopper.
 */
export function EquipmentPhoto({
  item,
  size = 48,
  radius = 8
}: {
  item: Pick<Equipment, 'name' | 'photoUrl'>
  size?: number
  radius?: number
}) {
  const url = useAuthedBlob(item.photoUrl ?? '')

  const frame: React.CSSProperties = {
    width: size,
    height: size,
    borderRadius: radius,
    flexShrink: 0,
    objectFit: 'cover',
    background: '#F1F5F9'
  }

  if (!url) {
    return (
      <div
        style={{
          ...frame,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          fontSize: Math.round(size * 0.45)
        }}
        aria-hidden="true"
      >
        🧰
      </div>
    )
  }

  return <img src={url} alt={`Foto af ${item.name}`} style={frame} />
}

/** "14. sep. 2026". Tom dato bliver til en tankestreg. */
export function formatDay(value?: string | null) {
  if (!value) return '-'
  const [y, m, d] = value.slice(0, 10).split('-').map(Number)
  return new Date(y, m - 1, d).toLocaleDateString('da-DK', { day: 'numeric', month: 'short', year: 'numeric' })
}

/**
 * Hvad køen betyder for den, der kigger: hvornår er grejet fri igen.
 *
 * Skrives som en hel sætning ét sted, så listen og værktøjets side ikke ender
 * med hver sin formulering af det samme.
 */
export function describeReservation(item: Equipment) {
  const next = item.nextReservation
  if (!next) return null

  const who = next.isMine ? 'Du har' : `${next.user.displayName} har`

  return next.isActiveNow
    ? `${who} reserveret det til og med ${formatDay(next.toDate)}`
    : `${who} reserveret det fra ${formatDay(next.fromDate)} til og med ${formatDay(next.toDate)}`
}
